import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Dashboard as CorkboardIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import NoteCard from '../../NoteCard';
import { selectCorkboards } from '../../../../../features/corkboards/corkboardsSlice';

// Predefined categories for notes
const NOTE_CATEGORIES = [
  'General',
  'Character',
  'Plot',
  'Setting',
  'Research',
  'Worldbuilding',
  'Dialogue',
  'Theme',
  'Conflict',
  'Other',
];

const NotesListView = ({ notes: standaloneNotes, onNoteSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Get all corkboards from Redux store
  const corkboards = useSelector(selectCorkboards);

  // Aggregate all notes from all sources
  const allNotes = useMemo(() => {
    const aggregatedNotes = [];

    // Add standalone notes (from notes slice)
    standaloneNotes.forEach((note) => {
      aggregatedNotes.push({
        ...note,
        source: {
          type: 'standalone',
          name: 'Notes',
          id: null,
        },
        category: note.category || note.type || 'General',
      });
    });

    // Add notes from all corkboards
    corkboards.forEach((corkboard) => {
      if (corkboard.notes && corkboard.notes.length > 0) {
        corkboard.notes.forEach((note) => {
          aggregatedNotes.push({
            ...note,
            source: {
              type: 'corkboard',
              name: corkboard.name || 'Untitled Corkboard',
              id: corkboard.id,
            },
            category: note.category || note.type || 'General',
          });
        });
      }
    });

    // Sort by most recently updated/created
    return aggregatedNotes.sort((a, b) => {
      const dateA = a.updatedEpoch || a.updatedAt || a.createdAt || 0;
      const dateB = b.updatedEpoch || b.updatedAt || b.createdAt || 0;
      return dateB - dateA;
    });
  }, [standaloneNotes, corkboards]);

  // Get unique sources for the filter dropdown
  const uniqueSources = useMemo(() => {
    const sources = new Set();
    allNotes.forEach((note) => {
      if (note.source.type === 'corkboard') {
        sources.add(`corkboard:${note.source.id}:${note.source.name}`);
      } else {
        sources.add('standalone:null:Notes');
      }
    });
    return Array.from(sources).map((source) => {
      const [type, id, name] = source.split(':');
      return { type, id: id === 'null' ? null : id, name };
    });
  }, [allNotes]);

  // Get unique categories from all notes
  const uniqueCategories = useMemo(() => {
    const categories = new Set(NOTE_CATEGORIES);
    allNotes.forEach((note) => {
      if (note.category) {
        categories.add(note.category);
      }
    });
    return Array.from(categories).sort();
  }, [allNotes]);

  // Filter notes based on search and filters
  const filteredNotes = useMemo(() => {
    return allNotes.filter((note) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = (note.title || '').toLowerCase().includes(query);
        const matchesContent = (note.content || '').toLowerCase().includes(query);
        const matchesTags = (note.tags || []).some((tag) => tag.toLowerCase().includes(query));
        if (!matchesTitle && !matchesContent && !matchesTags) {
          return false;
        }
      }

      // Source filter
      if (sourceFilter !== 'all') {
        if (sourceFilter === 'standalone' && note.source.type !== 'standalone') {
          return false;
        }
        if (sourceFilter === 'corkboards' && note.source.type !== 'corkboard') {
          return false;
        }
        if (sourceFilter.startsWith('corkboard:')) {
          const corkboardId = sourceFilter.replace('corkboard:', '');
          if (note.source.type !== 'corkboard' || note.source.id !== corkboardId) {
            return false;
          }
        }
      }

      // Category filter
      if (categoryFilter !== 'all') {
        if (note.category !== categoryFilter) {
          return false;
        }
      }

      return true;
    });
  }, [allNotes, searchQuery, sourceFilter, categoryFilter]);

  const handleNoteClick = (note) => {
    if (onNoteSelect) {
      onNoteSelect(note);
    }
  };

  // Count notes by source type
  const sourceCounts = useMemo(() => {
    const counts = { standalone: 0, corkboard: 0 };
    allNotes.forEach((note) => {
      if (note.source.type === 'standalone') {
        counts.standalone += 1;
      } else if (note.source.type === 'corkboard') {
        counts.corkboard += 1;
      }
    });
    return counts;
  }, [allNotes]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        All Notes
      </Typography>

      {/* Stats chips - clickable to filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<NoteIcon />}
          label={`${allNotes.length} total notes`}
          size="small"
          color="primary"
          variant={sourceFilter === 'all' ? 'filled' : 'outlined'}
          onClick={() => setSourceFilter('all')}
          sx={{ cursor: 'pointer' }}
        />
        {sourceCounts.standalone > 0 && (
          <Chip
            icon={<NoteIcon />}
            label={`${sourceCounts.standalone} standalone`}
            size="small"
            color="default"
            variant={sourceFilter === 'standalone' ? 'filled' : 'outlined'}
            onClick={() => setSourceFilter('standalone')}
            sx={{ cursor: 'pointer' }}
          />
        )}
        {sourceCounts.corkboard > 0 && (
          <Chip
            icon={<CorkboardIcon />}
            label={`${sourceCounts.corkboard} from corkboards`}
            size="small"
            color="warning"
            variant={sourceFilter === 'corkboards' ? 'filled' : 'outlined'}
            onClick={() => setSourceFilter('corkboards')}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 200, flexGrow: 1, maxWidth: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="source-filter-label">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FilterIcon fontSize="small" />
              Source
            </Box>
          </InputLabel>
          <Select
            labelId="source-filter-label"
            value={sourceFilter}
            label="Source"
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <MenuItem value="all">All Sources</MenuItem>
            <MenuItem value="standalone">Standalone Notes</MenuItem>
            <MenuItem value="corkboards">All Corkboards</MenuItem>
            {uniqueSources
              .filter((s) => s.type === 'corkboard')
              .map((source) => (
                <MenuItem key={source.id} value={`corkboard:${source.id}`}>
                  ↳ {source.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="category-filter-label">Category</InputLabel>
          <Select
            labelId="category-filter-label"
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {uniqueCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Notes Grid */}
      {filteredNotes.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: 2,
          }}
        >
          {filteredNotes.map((note) => (
            <Box
              key={`${note.source.type}-${note.source.id || 'standalone'}-${note._id || note.id || note.noteId}`}
              sx={{ height: 280 }}
            >
              <NoteCard note={note} onClick={handleNoteClick} showSource showCategory />
            </Box>
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: 'text.secondary',
            gap: 2,
          }}
        >
          {allNotes.length === 0 ? (
            <>
              <Typography variant="h6">No notes yet</Typography>
              <Typography variant="body2">Create a note to start organizing your ideas.</Typography>
            </>
          ) : (
            <>
              <Typography variant="h6">No matching notes</Typography>
              <Typography variant="body2">
                Try adjusting your search or filters to find what you&apos;re looking for.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

NotesListView.propTypes = {
  notes: PropTypes.arrayOf(PropTypes.object).isRequired,
  onNoteSelect: PropTypes.func,
};

NotesListView.defaultProps = {
  onNoteSelect: null,
};

export default NotesListView;
